/**
 * Section surface → text tone (single source of truth).
 *
 * Content declares surface / image / optional variant override.
 * Sections must use the *typography stacks* from sectionTextClasses —
 * not bare text-* color utilities (DS typography tokens bake color).
 *
 * Depth: use effects library `effect-drop-*` / text-pop aliases
 * (filter drop-shadow behind glyphs — not text-shadow outlines).
 *
 * Archive accents:
 *   highlight = green #2DAD71
 *   emphasis  = red   #e74c3c
 *
 * @typedef {'onDark' | 'onLight'} SectionTone
 */

/**
 * Resolve contrast tone from a content block.
 * Priority: explicit variant → surface → backgroundUrl → default light.
 *
 * @param {object | null | undefined} block
 * @returns {SectionTone}
 */
export function resolveSectionTone(block = {}) {
  if (!block || typeof block !== 'object') return 'onLight'

  if (block.variant === 'onDark' || block.variant === 'onLight') {
    return block.variant
  }
  if (block.variant === 'dark') return 'onDark'
  if (block.variant === 'light') return 'onLight'

  const surface = block.surface
  if (surface === 'beige' || surface === 'paper' || surface === 'light') return 'onLight'
  if (surface === 'blue' || surface === 'navy' || surface === 'dark') return 'onDark'
  if (surface === 'image') return 'onDark'

  if (block.backgroundUrl || block.imageUrl) return 'onDark'

  return 'onLight'
}

/**
 * Map titleParts.accent / line.tone to a DS color class.
 * true | 'highlight' | 'green' → green highlight
 * 'emphasis' | 'red' → archive red
 * 'primary' | 'bold' → navy primary + bold (process pillars)
 *
 * @param {boolean | string | undefined} accent
 * @returns {string} className or ''
 */
export function accentTextClass(accent) {
  if (!accent) return ''
  if (accent === 'primary' || accent === 'bold') {
    return 'text-color-primary font-bold'
  }
  if (accent === true || accent === 'highlight' || accent === 'green') {
    return 'text-color-highlight'
  }
  if (accent === 'emphasis' || accent === 'red') {
    return 'text-color-emphasis'
  }
  return ''
}

/**
 * Typography + CTA flags for a tone.
 * Prefer `display` / `body` / `sub` stacks (color baked in) over color-only classes.
 *
 * @param {SectionTone} tone
 */
export function sectionTextClasses(tone) {
  const onDark = tone === 'onDark'
  return {
    onDark,
    tone,
    /** Hero / FullCTA display headline + filter drop-shadow behind */
    display: onDark
      ? 'typography-hero-h1-mobile-on-dark md:typography-hero-large-on-dark effect-drop-display'
      : 'typography-hero-h1-mobile md:typography-hero-large effect-drop-display-light',
    /** Secondary display line (h3 scale) */
    sub: onDark
      ? 'typography-h3-mobile-on-dark md:typography-h3-on-dark effect-drop-md'
      : 'typography-h3-mobile md:typography-h3 effect-drop-soft',
    /** Body copy */
    body: onDark ? 'typography-body-lg-on-dark' : 'typography-body-lg',
    /** Default accent span = green highlight (archive) */
    accent: 'text-color-highlight',
    /** Red word-mark (archive "Andere") */
    emphasis: 'text-color-emphasis',
    // legacy aliases
    headline: onDark ? 'text-primary-on-dark' : 'text-color-primary',
    label: onDark ? 'text-secondary-on-dark' : 'text-color-secondary',
  }
}

/**
 * Default image overlay when content does not set `overlay`.
 * NOTE: custom @utility colors do NOT support Tailwind /opacity modifiers
 * (bg-color-primary/70 → transparent). Prefer resolveImageOverlay().
 * @param {SectionTone} tone
 */
export function sectionOverlayClass(tone) {
  // Prefer resolveImageOverlay() for real brand navy.
  // This Tailwind fallback is last-resort only (not used by FullCTA image path).
  return tone === 'onDark' ? 'bg-black/55' : 'bg-white/30'
}

/**
 * Reliable image darken for FullCTA / heroes.
 * Content may set:
 *   overlayOpacity: 0–1   (navy rgba by default)
 *   overlayColor: 'navy' | 'black' | css color
 *   overlay: Tailwind class (bg-color-primary/N rewritten to navy rgba)
 *
 * Default onDark image darken is brand navy rgba(34,65,96), never pure black,
 * unless content explicitly sets overlayColor: 'black'.
 *
 * @returns {{ className?: string, style?: { backgroundColor: string } }}
 */
export function resolveImageOverlay(block = {}, tone = 'onDark') {
  const onDark = tone === 'onDark'

  // Explicit opacity 0–1 → always works (no Tailwind opacity bug)
  if (block.overlayOpacity != null) {
    const o = Math.min(1, Math.max(0, Number(block.overlayOpacity)))
    if (Number.isFinite(o)) {
      const color = (block.overlayColor || 'navy').toString().trim().toLowerCase()
      let rgba
      if (color === 'black' || color === '#000' || color === '#000000') {
        rgba = `rgba(0, 0, 0, ${o})`
      } else if (color.startsWith('rgb')) {
        rgba = color.replace(/[\d.]+\)$/, `${o})`)
      } else if (color.startsWith('#') && color.length >= 7) {
        const h = color.slice(1)
        const r = parseInt(h.slice(0, 2), 16)
        const g = parseInt(h.slice(2, 4), 16)
        const b = parseInt(h.slice(4, 6), 16)
        rgba = `rgba(${r}, ${g}, ${b}, ${o})`
      } else {
        // brand navy #224160 — default for FullCTA / heroes (incl. 'navy')
        rgba = `rgba(34, 65, 96, ${o})`
      }
      return { style: { backgroundColor: rgba } }
    }
  }

  // Tailwind class path — rewrite custom-color opacity; pass through black/white only
  if (typeof block.overlay === 'string' && block.overlay.trim()) {
    const ov = block.overlay.trim()
    // Broken custom-color /opacity → brand navy rgba (not black)
    if (/bg-color-primary\/(\d+)/.test(ov)) {
      const pct = Number(RegExp.$1) / 100
      return { style: { backgroundColor: `rgba(34, 65, 96, ${pct})` } }
    }
    if (/bg-color-bg-light\/(\d+)/.test(ov)) {
      const pct = Number(RegExp.$1) / 100
      return { style: { backgroundColor: `rgba(237, 231, 220, ${pct})` } }
    }
    // Explicit black/white utilities stay as className; everything else → navy default
    if (/^bg-(black|white)\/\d+$/.test(ov)) {
      return { className: ov }
    }
    return {
      style: {
        backgroundColor: onDark ? 'rgba(34, 65, 96, 0.6)' : 'rgba(237, 231, 220, 0.35)',
      },
    }
  }

  return {
    style: {
      backgroundColor: onDark ? 'rgba(34, 65, 96, 0.6)' : 'rgba(237, 231, 220, 0.35)',
    },
  }
}
